import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Handshake, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SALES = ["super_admin", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc"];

export function AssignDealerButton({ lead, userRole, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const enabled = open && SALES.includes(userRole);

  const { data: dealers = [], isLoading } = useQuery({
    queryKey: ["my-dealers"],
    queryFn: async () => (await api.get("/dealers/my-dealers")).data,
    enabled,
  });

  if (!SALES.includes(userRole)) return null;

  const assign = async (d) => {
    setBusy(d.login_user_id);
    try {
      await api.put(`/leads/${lead.id}/assign`, { dealer_assigned_id: d.login_user_id });
      toast.success(`Handed to ${d.name} — now visible in their dealer portal`);
      setOpen(false); onAssigned?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="assign-dealer-btn">
          <Handshake className="h-4 w-4" /> {lead.dealer_assigned_id ? "Dealer" : "Hand to Dealer"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5" align="end" data-testid="assign-dealer-popover">
        {isLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : dealers.length === 0 ? (
          <p className="text-xs text-slate-500 p-3 text-center">No active dealers with a portal login yet.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {dealers.map((d) => (
              <button key={d.login_user_id} disabled={busy === d.login_user_id} onClick={() => assign(d)}
                className="w-full text-left flex items-center justify-between gap-2 px-2.5 py-2 text-sm rounded hover:bg-slate-100 transition-colors disabled:opacity-50"
                data-testid={`assign-dealer-${d.login_user_id}`}>
                <span className="truncate"><span className="font-medium">{d.name}</span> <span className="text-slate-400">· {d.zone}</span></span>
                {lead.dealer_assigned_id === d.login_user_id ? <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  : busy === d.login_user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> : null}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
